import { type ThemeProps } from '@kijiji/theme'
import Image from 'next/image'
import styled from 'styled-components'

import { BodyText } from '@/ui/atoms/body-text'
import { Button } from '@/ui/atoms/button'
import { Flex } from '@/ui/atoms/flex'
import { MEDIA_QUERIES } from '@/ui/constants/mediaQueries'
import { styleFromTypography } from '@/ui/helpers/styleFromTypography'

type WithInlineVariant = {
  useInlineVariant: boolean
}

const autoCanInlineFeaturesContainer = (theme: ThemeProps) => `
  gap: ${theme.spacing.large};
  grid-template-columns: 1fr 1fr;
`

export const AutoCanFeaturesContainer = styled.div<WithInlineVariant>(
  ({ theme, useInlineVariant }) => `
  display: grid;
  gap: ${theme.spacing.default};
  
  ${MEDIA_QUERIES(theme).medium} {
    ${useInlineVariant ? autoCanInlineFeaturesContainer(theme) : ''}
  }
`
)

/** Each Autocan Feature Block */
const autocanBadgeInlineWrapper = (theme: ThemeProps) => `
  align-items: center;
  height: calc(100% - ${theme.spacing.xLarge});
  margin-top: ${theme.spacing.xLarge};
`
export const AutocanBadgeWrapper = styled.div<WithInlineVariant>(
  ({ theme, useInlineVariant }) => `
  display: flex;
  
  ${MEDIA_QUERIES(theme).medium} {
    ${useInlineVariant ? autocanBadgeInlineWrapper(theme) : ''}
  }
`
)

const autocanBadgeInlineContainer = (theme: ThemeProps, bgColor: string) => `
  align-items: center;
  background-color: ${bgColor};
  flex-direction: column;
  overflow: visible;
  padding: ${theme.spacing.husky} ${theme.spacing.default} ${theme.spacing.default};
  position: relative;
`
export const AutocanBadgeContainer = styled(Flex)<
  WithInlineVariant & { bgColor: string; bgColorLight: string }
>(
  ({ theme, bgColor, bgColorLight, useInlineVariant }) => `
  background-color: ${bgColorLight};
  border-radius: ${theme.borderRadius.small};
  border: .1rem solid ${theme.colors.grey.light4};
  overflow: hidden;
  width: 100%;
  height: 100%;
  
  ${MEDIA_QUERIES(theme).medium} {
    background-color: ${theme.colors.purple.primary};
    ${useInlineVariant ? autocanBadgeInlineContainer(theme, bgColor) : ''}
  }
`
)

const autocanBadgeInlineIllustrationContainer = `
  margin-right: 0;
  position: absolute;
  top: 0;
`
export const AutocanBadgeIllustrationContainer = styled.div<WithInlineVariant>(
  ({ theme, useInlineVariant }) => `
  height: 100%;
  position: relative;
  width: 12rem;
  
  ${MEDIA_QUERIES(theme).medium} {
    width: 13.5rem;
    ${useInlineVariant ? autocanBadgeInlineIllustrationContainer : ''}
  }
`
)

const autocanBadgeInlineImage = (theme: ThemeProps) => `
  height: 7.8rem;
  left: 50%;
  min-height: unset;
  position: relative;
  transform: translate(-50%, -50%);
  width: 10rem;

  & img {
    border-radius: ${theme.borderRadius.medium};
  }
`
export const AutocanBadgeImage = styled.div<
  WithInlineVariant & { hasListingImage?: boolean; bgColor: string }
>(
  ({ theme, useInlineVariant, hasListingImage, bgColor }) => `
  align-items: flex-end;
  background-color: ${bgColor};
  display: flex;
  height: 100%;
  justify-content: center;
  min-height: 9.3rem;
  position: relative;
  width: 100%;
  
  ${MEDIA_QUERIES(theme).medium} {
    background-color: unset;
    margin-right: ${hasListingImage ? theme.spacing.husky : theme.spacing.default};
    width: 11rem;
    ${useInlineVariant ? autocanBadgeInlineImage(theme) : ''}    
  }`
)

const autocanInlineLocalImage = `
  left: -3rem;
  margin-left: 0;
  width: unset;
`

// eslint-disable-next-line @kijiji/prefer-image-custom
export const AutocanLocalImage = styled.img<WithInlineVariant & { bgColor: string }>(
  ({ theme, useInlineVariant }) => `
  margin-bottom: ${theme.spacing.mini};
  width: 5.7rem;

  ${MEDIA_QUERIES(theme).medium} {
    background-color: unset;
    height: 100%;
    margin-left: ${theme.spacing.default};

    width: 9.7rem;
    ${useInlineVariant ? autocanInlineLocalImage : ''}
  }
`
)

const autocanBadgeInlineImageIcon = `
  height: 5.6rem;
  left: calc(50% + 5rem);
  position: absolute;
  transform: translate(-50%, -50%);
  width: 5.6rem;
`
export const AutocanBadgeImageIcon = styled(Image)<{ $useInlineVariant: boolean }>(
  ({ theme, $useInlineVariant }) => `
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(50%, -50%);

  ${MEDIA_QUERIES(theme).medium} {
    ${$useInlineVariant ? autocanBadgeInlineImageIcon : ''}
  }
`
)

const autocanBadgeInlineContent = (theme: ThemeProps) => `
  align-items: center;
  gap: ${theme.spacing.large};
  justify-content: center;
  flex-direction: column;

  & p {
    text-align: center;
  }
`
export const AutocanBadgeContent = styled.div<WithInlineVariant>(
  ({ theme, useInlineVariant }) => `
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing.default};
  width: 100%;
  
  // Chevron definition
  & > svg {
    height: 2.4rem;
    width: 2.4rem;
    }
    
    ${MEDIA_QUERIES(theme).medium} {
    padding: ${theme.spacing.default} ${theme.spacing.default} ${theme.spacing.default} 0;
    ${useInlineVariant ? autocanBadgeInlineContent(theme) : ''}
    
    // Chevron definition
    & > svg {
      display: none;
    }
  }
`
)

export const AutocanBadgeTitle = styled(BodyText)<{ hasShortTitle: boolean }>(
  ({ theme, hasShortTitle }) => `
  & span:first-of-type {
    display: ${hasShortTitle ? 'none' : 'block'};
  }


  ${MEDIA_QUERIES(theme).medium}{
    ${styleFromTypography(theme.typography.headline.large)}
    color: ${theme.colors.white};
    font-weight: 400;

    & span:first-of-type {
      display: block;
    }

    & span:nth-child(2) {
      display: none;
    }
  }
`
)

export const AutocanBadgeSubtitle = styled(BodyText)(
  ({ theme }) => `
  & span:first-of-type {
    display: none;
  }

  ${MEDIA_QUERIES(theme).medium}{
    color: ${theme.colors.white};

    & span:first-of-type {
      display: block;
    }

    & span:nth-child(2) {
      display: none;
    }
  }
`
)

const autocanInlineExternalLink = `
  & svg {
    display: block;
  }
`
export const AutocanExternalLink = styled(Button)<WithInlineVariant>(
  ({ theme, useInlineVariant }) => `
  align-self: center;
  display: none;
  height: min-content;
  margin-left: ${theme.spacing.default};
  position: relative;
  white-space: nowrap;
  width: fit-content;

  & svg {
    display: none;
  }
  
  ${MEDIA_QUERIES(theme).medium} {
    display: flex;
    ${useInlineVariant ? autocanInlineExternalLink : ''}
  }
`
)
